import dynamic from 'next/dynamic'
import Link from 'next/link'
import React, { useCallback, useEffect, useRef, useState } from 'react'
import { deferToIdle } from 'src/utils/deferToIdle'
import ga from 'src/utils/pixels/gtag'
import { SearchFilters } from '../SearchBar/types'
import SpotMeLogo from '../SpotMeLogo'
// Always-visible nav children — direct imports so SSR HTML matches the
// client tree exactly. Wrapping these in `dynamic()` (without ssr:false)
// introduced React 19 streaming-Suspense hydration mismatches (#418) on
// `<DesktopMegaMenu>` — the server emitted an older chunk's output while
// the client rendered the current version. SearchBar was converted to a
// direct import as defensive hardening alongside this rule.
//
// The historical `<input type="text">` SSR vs no-`type` CSR drift was
// initially misattributed to this dynamic-import pattern, but at-error
// captureHydrationDiff later proved the real root cause is a React 19
// default-attribute behavior on raw `<input>` elements — see SearchBar/
// index.tsx for the canonical fix (drop the explicit `type="text"` since
// it matches the browser default and CSR DOM construction omits it).
import SearchBar from '../SearchBar'
import DesktopButtons from './DesktopButtons'
import DesktopMegaMenu, { type MegaMenuOpenVia } from './DesktopMegaMenu'

const Drawer = dynamic(() => import('./Drawer'), { ssr: false })
const LandingNotificationsWrapper = dynamic(
  () => import('./LandingNotificationsWrapper'),
  { ssr: false }
)

/** Grace period before a hover-opened panel closes once the pointer leaves
 *  it — long enough to cross the gap between button and panel. */
const HOVER_CLOSE_DELAY_MS = 150
/** Hover intent: a pointer crossing the bar on its way to the host pill
 *  used to open and close three panels. Clicks skip this. */
const HOVER_OPEN_DELAY_MS = 100

type IProp = {
  showSearch?: boolean
  transparent?: boolean
  noShadow?: boolean
  secondRow?: React.ReactNode
  isAnfitrion?: boolean
  onSearch?: (filters: SearchFilters) => void
  className?: string
  /**
   * Linear funnels (the host calculator): logo only, not linked — no mega menu, links or drawer to
   * leave through mid-flow. Same rule as StepLayout's checkout/KYC shell.
   */
  minimal?: boolean
}

/**
 * Global marketing nav.
 *
 * Breakpoints: below `lg` (1024) the hamburger + Drawer carry the site map;
 * from `lg` up the three mega menus do. It used to switch at `md` (900)
 * while two of the three menus only rendered from `lg`, so a 900–1023px
 * viewport (iPad landscape, a small laptop window) had no path to the
 * product menus at all — fixed 2026-09-09.
 *
 * Chrome: solid white + hairline, no blur and no drop shadow — the same
 * vocabulary as the search-page bar and every editorial surface below it.
 * The old `bg-white-500/95 backdrop-blur-md shadow-sm` was a fixed,
 * full-width backdrop-filter on every marketing page, the GPU pattern that
 * caused scroll jank on `/anfitrion` before.
 */
const LandingNav = ({
  showSearch,
  transparent,
  noShadow,
  secondRow,
  isAnfitrion,
  onSearch,
  className,
  minimal
}: IProp) => {
  const [isOpen, setIsOpen] = useState(false)
  const [activeDropdown, setActiveDropdown] = useState<string | null>(null)
  // Mirrors `activeDropdown` so the handlers below can read the current
  // value without being recreated on every open/close.
  const activeRef = useRef<string | null>(null)
  const closeTimeout = useRef<NodeJS.Timeout | null>(null)
  // Set when the open came from hover. The first click on a menu that hover
  // already opened must NOT toggle it closed: touch devices synthesize
  // mouseenter right before click, and a mouse user clicking the button
  // they are hovering expects the panel to stay. The flag is consumed by
  // that first click; the next one closes.
  const openedByHover = useRef(false)

  const clearCloseTimer = () => {
    if (closeTimeout.current) clearTimeout(closeTimeout.current)
    closeTimeout.current = null
  }

  const setActive = useCallback((id: string | null) => {
    activeRef.current = id
    setActiveDropdown(id)
  }, [])

  const openDropdown = useCallback(
    (id: string, via: MegaMenuOpenVia) => {
      clearCloseTimer()
      if (activeRef.current !== id) {
        // Off the interaction — see deferToIdle. Fired once per open, not per
        // hover jitter, because re-entering the same open menu is a no-op.
        deferToIdle(() => ga.event('nav_megamenu_open', { menu: id, via }))
      }
      openedByHover.current = via === 'hover'
      setActive(id)
    },
    [setActive]
  )

  const closeDropdown = useCallback(() => {
    clearCloseTimer()
    openedByHover.current = false
    setActive(null)
  }, [setActive])

  const openTimeout = useRef<NodeJS.Timeout | null>(null)
  const clearOpenTimer = () => {
    if (openTimeout.current) clearTimeout(openTimeout.current)
    openTimeout.current = null
  }

  const handleMouseEnter = useCallback(
    (id: string) => {
      clearCloseTimer()
      // Re-entering the open panel (or its button) must not re-arm a delay.
      if (activeRef.current === id) return
      clearOpenTimer()
      // Switching between two open menus is immediate — the intent is clear.
      if (activeRef.current) {
        openDropdown(id, 'hover')
        return
      }
      openTimeout.current = setTimeout(
        () => openDropdown(id, 'hover'),
        HOVER_OPEN_DELAY_MS
      )
    },
    [openDropdown]
  )

  const handleMouseLeave = useCallback(() => {
    clearOpenTimer()
    clearCloseTimer()
    closeTimeout.current = setTimeout(
      () => setActive(null),
      HOVER_CLOSE_DELAY_MS
    )
  }, [setActive])

  // Click / Enter / Space / touch tap on a menu button.
  const handleToggle = useCallback(
    (id: string) => {
      if (activeRef.current === id) {
        if (openedByHover.current) {
          openedByHover.current = false
          return
        }
        closeDropdown()
        return
      }
      openDropdown(id, 'click')
    },
    [openDropdown, closeDropdown]
  )

  // Escape closes an open panel from anywhere on the page.
  useEffect(() => {
    if (!activeDropdown) return
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') closeDropdown()
    }
    document.addEventListener('keydown', onKeyDown)
    return () => document.removeEventListener('keydown', onKeyDown)
  }, [activeDropdown, closeDropdown])

  useEffect(
    () => () => {
      clearCloseTimer()
      clearOpenTimer()
    },
    []
  )

  const isTransparent = Boolean(transparent) && !isOpen
  const hairline =
    noShadow || isOpen || isTransparent ? '' : 'border-b border-ink-100/15'

  return (
    <div className="w-full">
      <nav
        className={`fixed top-0 z-[1299] flex h-(--nav-h) w-full flex-col transition-colors duration-300 ${
          isTransparent ? 'bg-transparent' : 'bg-white-500'
        } ${hairline} ${className ?? ''}`}
      >
        <div
          className={`mx-auto flex size-full max-w-screen-xxl items-center justify-between px-5 md:px-8 lg:px-12 ${
            secondRow ? 'border-b border-ink-100/15' : ''
          }`}
        >
          {/* Logo */}
          <div className="flex shrink-0 items-center">
            {/* prefetch={false} across the nav: the logo alone prefetched
                the home route (7 chunks + data JSON) on every page a visitor
                landed on; navigation still prefetches on hover/touchstart. */}
            {minimal ? (
              <SpotMeLogo />
            ) : (
              <Link
                prefetch={false}
                href="/"
                passHref
                className="transition-opacity hover:opacity-80"
              >
                <SpotMeLogo />
              </Link>
            )}
          </div>

          {/* Search bar (when enabled) */}
          {showSearch && !minimal && <SearchBar onSearch={onSearch} />}

          {/* Desktop mega menu */}
          {!showSearch && !minimal && (
            <DesktopMegaMenu
              activeDropdown={activeDropdown}
              onHoverEnter={handleMouseEnter}
              onHoverLeave={handleMouseLeave}
              onToggle={handleToggle}
              onClose={closeDropdown}
            />
          )}

          {/* Desktop buttons */}
          {!minimal && (
            <DesktopButtons hideLinks={showSearch} isAnfitrion={isAnfitrion} />
          )}

          {/* Menu button — below lg the Drawer is the site map */}
          <div className={minimal ? 'hidden' : 'lg:hidden'}>
            <button
              onClick={() => setIsOpen(!isOpen)}
              className="relative flex size-11 items-center justify-center rounded-xl transition-colors duration-200 hover:bg-grey-250 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-400"
              aria-label={isOpen ? 'Close menu' : 'Open menu'}
              aria-expanded={isOpen}
            >
              <div className="flex h-[18px] w-6 flex-col justify-between">
                <span
                  className={`block h-[2px] w-full origin-center rounded-full transition-all duration-300 ease-[cubic-bezier(0.77,0,0.18,1)] ${
                    isOpen
                      ? 'translate-y-[8px] rotate-45 bg-blue-500'
                      : 'bg-black-500'
                  }`}
                />
                <span
                  className={`block h-[2px] rounded-full bg-black-500 transition-all duration-300 ease-[cubic-bezier(0.77,0,0.18,1)] ${
                    isOpen ? 'w-0 opacity-0' : 'w-full'
                  }`}
                />
                <span
                  className={`block h-[2px] w-full origin-center rounded-full transition-all duration-300 ease-[cubic-bezier(0.77,0,0.18,1)] ${
                    isOpen
                      ? '-translate-y-[8px] -rotate-45 bg-blue-500'
                      : 'bg-black-500'
                  }`}
                />
              </div>
              <LandingNotificationsWrapper />
            </button>
          </div>
        </div>

        {/* Mobile / tablet drawer */}
        {!minimal && <Drawer isOpen={isOpen} setIsOpen={setIsOpen} />}

        {secondRow}
      </nav>
    </div>
  )
}

export default React.memo(LandingNav)
